"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingDown, SearchX, Layers, ShoppingBag, ArrowRight, ShieldAlert, Compass, CircleAlert as AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const challengeHighlights = [{
  status: "Stagnant",
  label: "Organic Traffic Growth",
  detail: "Non-brand organic search sessions flatlined over consecutive quarters despite catalogue expansions."
}, {
  status: "Low Visibility",
  label: "High-Intent Search Terms",
  detail: "Core commercial category queries failed to reach top search rankings against competitors."
}, {
  status: "Sub-Optimal",
  label: "Landing Page Performance",
  detail: "Key collection hubs and category pages underperformed in organic discoverability and search engagement."
}, {
  status: "Plateaued",
  label: "Organic Revenue Impact",
  detail: "Top-line organic revenue stalled despite the client's strong brand reputation and high-quality products."
}];
const coreChallenges = [{
  icon: TrendingDown,
  title: "Stagnant Organic Traffic",
  description: "Despite regular product launches and active customer interest, non-brand organic search traffic remained flat, preventing the business from scaling new customer acquisition through search.",
  tag: "Traffic Growth"
}, {
  icon: SearchX,
  title: "Poor High-Intent Keyword Visibility",
  description: "The retailer struggled to gain meaningful visibility for high-volume commercial keywords, leaving high-converting search demand captured almost entirely by competing retail brands.",
  tag: "Search Visibility"
}, {
  icon: Layers,
  title: "Underperforming Landing Pages",
  description: "Primary collection and category landing pages were not structured effectively for search engines, limiting topical relevance, internal link equity distribution, and index quality.",
  tag: "Site Architecture"
}, {
  icon: ShoppingBag,
  title: "Revenue Plateau Despite Strong Brand",
  description: "The brand had earned high customer loyalty and product praise, but organic search was underdelivering as a primary revenue driver, causing overall business growth to stall.",
  tag: "Commercial Impact"
}];
export default function FashionChallenge() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="fashionretailerchallenge" data-nav="light" className="bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Heading */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-3xl mb-14 md:mb-18">
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              Case Study: Fashion Retailer
            </Badge>
            <span className="text-xs text-muted-foreground">Phase 1: The Diagnostics</span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-foreground mb-6">
            The Key Bottlenecks Holding Organic Revenue Back
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            When this mid-sized online fashion retailer reached out to me, they were dealing with stagnant organic traffic, 
            poor visibility for high-intent keywords, and underperforming landing pages. Despite possessing a well-respected brand 
            and high-quality products, their organic revenue was plateauing.
          </p>
        </motion.div>

        {/* Challenge Highlights Metric Bar */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-16">
          {challengeHighlights.map((item, i) => <div data-index={i} key={item.label}>
              <motion.div variants={itemVariants} className="h-full">
                <Card className="h-full border border-border bg-card shadow-xs transition-colors hover:border-primary/40">
                  <CardHeader className="pb-2">
                    <span className="text-2xl sm:text-3xl font-serif font-bold text-accent tracking-tight">
                      {item.status}
                    </span>
                    <CardTitle className="text-sm font-semibold text-foreground pt-1">
                      {item.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {item.detail}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>)}
        </motion.div>

        {/* Detailed Breakdown & Assessment */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">
          {/* Core Challenges Cards */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true
        }} className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {coreChallenges.map((challenge, i) => {
            const IconComp = challenge.icon;
            return <div data-index={i} key={challenge.title}>
                  <motion.div variants={itemVariants} className="h-full">
                    <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-colors duration-200">
                      <CardHeader className="gap-3">
                        <div className="flex items-center justify-between">
                          <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                            <IconComp className="size-5" />
                          </div>
                          <Badge variant="outline" className="text-[13px] font-normal border-border bg-background">
                            {challenge.tag}
                          </Badge>
                        </div>
                        <CardTitle className="text-lg font-bold text-foreground">
                          {challenge.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                          {challenge.description}
                        </CardDescription>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>;
          })}
          </motion.div>

          {/* Consultant Diagnosis Sidebar */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-4">
            <div className="rounded-xl border border-border bg-card p-6 sm:p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2 rounded-lg bg-accent/20 text-accent">
                  <ShieldAlert className="size-5" />
                </div>
                <h3 className="font-serif font-bold text-lg text-foreground">
                  My Initial Assessment
                </h3>
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                {"“The store had high-calibre products and strong brand affinity, but their organic search presence was completely stalled. High-intent commercial queries were missing page one, and key landing pages were not structured to capture buying demand.”"}
              </p>

              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                {"Without rebuilding the keyword targeting architecture and addressing landing page underperformance, marketing spend would continue to yield diminishing organic returns."}
              </p>

              <Separator className="my-5" />

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-xs text-foreground font-medium">
                  <Compass className="size-4 text-primary" />
                  <span>Direct Consultant Diagnosis by Yassine Essaadi</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <AlertCircle className="size-4 text-accent" />
                  <span>Verified findings from initial search performance audit</span>
                </div>
              </div>

              <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-medium">
                <Link to="/CaseStudiesPage">
                  View Full Strategy & Results
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}